import { useCallback, useEffect, useRef, useState } from 'react';
import { useHydrated } from 'remix-utils';

interface RecentlySearched {
    recentlySearched: string[];
    addRecentSearch: (x: string) => void;
    clearHistory: () => void;
}

interface UseRecentlySearched {
    (): RecentlySearched;
}

const MAX_LOCAL_SEARCH_STORAGE = 5;
const RECENTLY_SEARCHED_KEY = 'recently-searched';

export const useRecentlySearched: UseRecentlySearched = () => {
    const hydrated = useHydrated();
    const [recentlySearched, setRecentlySearched] = useState<string[]>([]);
    const initialLoadDone = useRef(false);

    // Load initial data from localStorage
    useEffect(() => {
        if (!hydrated || initialLoadDone.current) return;

        try {
            const storedSearches = localStorage.getItem(RECENTLY_SEARCHED_KEY);
            if (storedSearches) {
                const parsed = JSON.parse(storedSearches);
                if (Array.isArray(parsed)) {
                    // Guard against non-string entries that may have been stored by a prior bug.
                    const validStrings = parsed.filter(
                        (item): item is string => typeof item === 'string',
                    );
                    setRecentlySearched(validStrings);
                    if (validStrings.length !== parsed.length) {
                        localStorage.setItem(
                            RECENTLY_SEARCHED_KEY,
                            JSON.stringify(validStrings),
                        );
                    }
                }
            }
            initialLoadDone.current = true;
        } catch (error) {
            console.warn(
                'Failed to load recently searched items from localStorage:',
                error,
            );
            // Clear corrupted data
            try {
                localStorage.setItem(RECENTLY_SEARCHED_KEY, '[]');
            } catch (clearError) {
                console.warn(
                    'Failed to clear corrupted localStorage data:',
                    clearError,
                );
            }
            initialLoadDone.current = true;
        }
    }, [hydrated]);

    // Listen for localStorage changes from other tabs
    useEffect(() => {
        if (!hydrated) return;

        const handleStorageChange = (e: StorageEvent) => {
            // Only respond to changes to our specific key
            if (e.key === RECENTLY_SEARCHED_KEY) {
                try {
                    if (e.newValue === null) {
                        // Key was removed
                        setRecentlySearched([]);
                    } else {
                        const parsed = JSON.parse(e.newValue);
                        if (Array.isArray(parsed)) {
                            setRecentlySearched(parsed);
                        }
                    }
                } catch (error) {
                    console.warn(
                        'Failed to parse storage change event:',
                        error,
                    );
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [hydrated]);

    const addRecentSearch = useCallback(
        (search: string) => {
            if (!search) return;

            // Get fresh data from localStorage to avoid race conditions
            let currentSearches: string[] = [];
            try {
                const storedSearches = localStorage.getItem(
                    RECENTLY_SEARCHED_KEY,
                );
                if (storedSearches) {
                    const parsed = JSON.parse(storedSearches);
                    if (Array.isArray(parsed)) {
                        currentSearches = parsed;
                    }
                }
            } catch (error) {
                console.warn(
                    'Failed to read current searches from localStorage:',
                    error,
                );
                currentSearches = recentlySearched; // Fallback to state
            }

            if (currentSearches.includes(search)) return; // Prevent adding duplicates

            try {
                const searches = [search, ...currentSearches];
                const result = searches.slice(0, MAX_LOCAL_SEARCH_STORAGE);
                setRecentlySearched(result);
                localStorage.setItem(
                    RECENTLY_SEARCHED_KEY,
                    JSON.stringify(result),
                );
            } catch (error) {
                console.warn(
                    'Failed to save recent search to localStorage:',
                    error,
                );
                // Still update state even if localStorage fails
                const searches = [search, ...currentSearches];
                const result = searches.slice(0, MAX_LOCAL_SEARCH_STORAGE);
                setRecentlySearched(result);
            }
        },
        [recentlySearched],
    );

    const clearHistory = useCallback(() => {
        setRecentlySearched([]);
        initialLoadDone.current = false;

        if (typeof window !== 'undefined') {
            try {
                localStorage.removeItem(RECENTLY_SEARCHED_KEY);
            } catch (error) {
                console.warn(
                    'Failed to clear recently searched items from localStorage:',
                    error,
                );
            }
        }
    }, []);

    return {
        recentlySearched: hydrated ? recentlySearched : [],
        addRecentSearch,
        clearHistory,
    };
};
